import { push as pushApi } from '../api/endpoints.ts';
import { createStore } from '../core/store.ts';
import { currentLang } from '../i18n/index.ts';

/**
 * Push в браузере.
 *
 * Разрешение спрашивается после первого оплаченного заказа через лист-объяснение
 * (`PushPrimerSheet`): тогда понятно, зачем уведомления. До этого браузер подписывается, только
 * если разрешение уже выдано раньше. Подписка регистрируется на сервере как устройство
 * аккаунта и снимается при выходе.
 */
export const pushStore = createStore<{ primerPending: boolean }>({ primerPending: false });

/** Имя кеша и ключ, по которым service worker читает язык интерфейса. */
const CONFIG_CACHE = 'trotty10-config';
const LANG_KEY = '/__lang';

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

function worker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return Promise.resolve(null);
  // В разработке worker отдаётся модулем прямо из исходников, в сборке — одним файлом в корне.
  registration ??= (import.meta.env.DEV
    ? navigator.serviceWorker.register('/src/sw.ts', { type: 'module' })
    : navigator.serviceWorker.register('/sw.js')
  ).catch(() => null);
  return registration;
}

/** Подписаться можно только на работающий worker: сразу после регистрации он ещё ставится. */
async function activated(active: ServiceWorkerRegistration): Promise<void> {
  for (let attempt = 0; attempt < 40 && !active.active; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
}

/** Открытый ключ отправителя; `null` — push в браузер на сервере не настроен. */
let serverKey: Promise<string | null> | null = null;

function publicKey(): Promise<string | null> {
  serverKey ??= pushApi
    .webKey()
    .then((response) => response.publicKey)
    .catch(() => {
      serverKey = null;
      return null;
    });
  return serverKey;
}

/** Язык интерфейса для service worker: у него нет доступа к хранилищу страницы. */
export function sharePushLang(): void {
  if (!('caches' in window)) return;
  void caches
    .open(CONFIG_CACHE)
    .then((cache) => cache.put(LANG_KEY, new Response(currentLang())))
    .catch(() => undefined);
}

/**
 * Запуск: регистрация service worker и приём переходов по тапу на уведомление. `open` получает
 * строку параметров ссылки запуска (`order=<id>`).
 */
export function startPush(open: (query: string) => void): void {
  if (!isPushSupported()) return;
  void worker();
  sharePushLang();
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string; query?: string } | null;
    if (data?.type === 'trotty10-open' && typeof data.query === 'string') open(data.query);
  });
}

/** Подписка браузера уходит на сервер, если разрешение есть. Вызывается после входа и запуска. */
export async function syncPushSubscription(): Promise<void> {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  const [active, key] = await Promise.all([worker(), publicKey()]);
  if (!active || !key) return;
  try {
    await activated(active);
    const subscription =
      (await active.pushManager.getSubscription()) ?? (await active.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }));
    const json = subscription.toJSON();
    if (json.endpoint && json.keys?.p256dh && json.keys.auth) {
      await pushApi.register({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
    }
  } catch {
    // Браузер отказал в подписке или сервер не ответил: попытка повторится на следующем запуске.
  }
}

/** Первый заказ оплачен, а разрешение ещё не спрашивали: поднимается лист-объяснение. */
export async function offerPushPrimer(): Promise<void> {
  if (!isPushSupported() || Notification.permission !== 'default') return;
  if ((await publicKey()) === null) return;
  pushStore.set({ primerPending: true });
}

export function dismissPushPrimer(): void {
  pushStore.set({ primerPending: false });
}

/** Системный вопрос браузера. После отказа он второй раз не спрашивает: решение меняют в настройках. */
export async function requestPushPermission(): Promise<void> {
  if (!isPushSupported()) return;
  const permission = await Notification.requestPermission().catch(() => 'denied' as const);
  if (permission === 'granted') await syncPushSubscription();
}

/** Выход: подписка снимается с аккаунта до отзыва сессии и гасится в браузере. */
export async function removePushSubscription(): Promise<void> {
  if (!isPushSupported() || registration === null) return;
  const active = await registration;
  const subscription = await active?.pushManager.getSubscription().catch(() => null);
  if (!subscription) return;
  await pushApi.remove(subscription.endpoint).catch(() => undefined);
  await subscription.unsubscribe().catch(() => undefined);
}
